import type { ReactElement } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { QueueItem } from '../hooks/useBulkInvoiceQueue'

interface BulkQueueNavProps {
  items: QueueItem[]
  currentIndex: number
  onNext: () => void
  onPrev: () => void
  onJump: (id: string) => void
}

function statusOf(item: QueueItem): string {
  if (item.saveStatus === 'saved') return 'saved'
  if (item.saveStatus === 'discarded') return 'discarded'
  if (item.saveStatus === 'failed') return 'failed'
  if (item.parseStatus === 'failed') return 'failed'
  if (item.parseStatus === 'parsing' || item.parseStatus === 'pending') return 'parsing'
  return 'ready'
}

const DOT_CLASSES: Record<string, string> = {
  parsing: 'bg-muted',
  ready: 'bg-primary/70',
  saved: 'bg-(--border-positive)',
  discarded: 'bg-muted-foreground/40',
  failed: 'bg-destructive',
}

export function BulkQueueNav({
  items,
  currentIndex,
  onNext,
  onPrev,
  onJump,
}: BulkQueueNavProps): ReactElement {
  const atStart = currentIndex <= 0
  const atEnd = currentIndex >= items.length - 1

  return (
    <div className="flex flex-col gap-2 border-b border-border/40 pb-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Invoice {currentIndex + 1} of {items.length}
        </span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Previous invoice"
            disabled={atStart}
            onClick={onPrev}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Next invoice"
            disabled={atEnd}
            onClick={onNext}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {items.map((it, idx) => {
          const status = statusOf(it)
          const isCurrent = idx === currentIndex
          return (
            <button
              key={it.id}
              type="button"
              aria-label={`Jump to invoice ${idx + 1}`}
              data-status={status}
              data-current={isCurrent ? 'true' : 'false'}
              onClick={() => onJump(it.id)}
              className={cn(
                'h-2.5 w-2.5 rounded-full transition-all',
                DOT_CLASSES[status],
                isCurrent && 'ring-2 ring-foreground/60 ring-offset-1 ring-offset-background',
              )}
            />
          )
        })}
      </div>
    </div>
  )
}
